import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { useNavigate, useLocation } from "react-router-dom";
import PaperBox from "bundles/common/components/PaperBox";
import JobOfferList from "bundles/Candidate/components/JobOfferList";
import sh from "bundles/common/utils/sh";
import BreadcrumbsBar from "./BreadcrumbsBar";

const useQuery = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const PublicJobOfferIndex = () => {
  const intl = useIntl();
  const query = useQuery();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const [jobOffers, setJobOffers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [count, setCount] = useState(0);
  const i18nLocale = useSelector((state) => state.i18nLocale) || "en";

  useEffect(() => {
    if (
      !selectedJob &&
      !query.get("jid") &&
      !query.get("selectedJobId")
    ) {
      setSelectedJob(jobOffers.length > 0 ? jobOffers[0] : null);
    }
  }, [jobOffers.length]);

  const fetchJobOffers = ({
    page = 1,
    callback,
  } = {}) => {
    sh.get(`/${i18nLocale}/job-offers/fetch`, {
      params: {
        authenticity_token: authenticityToken,
        page,
      },
    })
      .then((res) => {
        console.log("RESPONSE:", res.data);
        setJobOffers(res.data?.data || []);
        let hasJobQueryParam = query.get("jid") || query.get("selectedJobId");
        if (
          (res?.data?.pagy?.count || 0) > 0 &&
          !hasJobQueryParam
        ) {
          setSelectedJob(res.data?.data?.job_offers?.[0] || null);
        } else {
          setSelectedJob(selectedJob);
        }
        setPageCount(res.data?.pagy?.pages);
        setCount(res?.data?.pagy?.count);
        callback && callback(res.data?.data);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleOfferClick = (offer) => {
    if (offer) {
      navigate(`/${i18nLocale}/job-offers/${offer.id}`);
    }
  };

  console.log("OFFERS:", jobOffers);

  const JobCount = () => (
    <PaperBox color="primary" variant="contained">
      <Typography variant="h6">
        <b>
          {intl.formatMessage({ id: "job_search.n_results" }, {
            result: count || jobOffers.length
          })}
        </b>
      </Typography>
    </PaperBox>
  );

  useEffect(() => {
    fetchJobOffers({
      page,
    });
  }, [
    page,
  ]);

  const SECTORS = [
    { name: "Architecture", path: `/${i18nLocale}/job-offers/architecture`},
    { name: "Engineering", path: `/${i18nLocale}/job-offers/engineering`},
    { name: "Construction", path: `/${i18nLocale}/job-offers/construction`},
    { name: "Administration", path: `/${i18nLocale}/job-offers/administration`},
  ];

  const crumbs = [
    { label: "Home", path: `/${i18nLocale}` },
    { label: "Job Offers", path: `/${i18nLocale}/job-offers`, current: true },
  ];

  return (
    <>
      <Box textAlign="center" mt={4} mb={4}>
        <Typography variant="h1">
          <FormattedMessage id="public_job_offer_index.main_index_header" />
        </Typography>
        <Typography variant="subtitle1">
          <FormattedMessage id="public_job_offer_index.main_index_subtitle" />
        </Typography>
      </Box>

      <Container maxWidth="lg">
        <BreadcrumbsBar crumbs={crumbs} />
      </Container>

      {/* Industry Buttons */}
      <Container maxWidth="md">
        <Grid container spacing={2} mt={2} mb={4} justifyContent="center">
          {SECTORS.map((sector) => (
            <Grid item xs={6} md={3} key={sector.path}>
              <a href={sector.path} style={{ textDecoration: "none" }}>
                <Button fullWidth variant="contained" color="primary">
                  {sector.name}
                </Button>
              </a>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* JOB OFFER LIST SECTION */}
      <Container maxWidth="lg">
        <Grid container spacing={4} style={{ marginTop: 16, marginBottom: 16 }}>
          <Grid item xs={12}>
            {/* Show total count */}
            <Box mb={2}>
              <JobCount />
            </Box>
            {/* The existing JobOfferList (like candidate pages) */}
            <Box style={{ height: "100%" }}>
              <JobOfferList
                offers={jobOffers}
                activeOffer={selectedJob || null}
                handleOfferClick={handleOfferClick}
                columnPerRow={isDesktop ? 2 : 1}
                setPage={setPage}
                page={page}
                pageCount={pageCount}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default PublicJobOfferIndex;
